/* eslint-disable no-console */
import { Client, IMessage } from '@stomp/stompjs';

import { formatTopicId, formatTopicPath } from '@webtrader/core/format/socket';

import { ISocketTopic } from './index';

interface IProps {
  client: Client;
}

export interface INewsTopicReceiver {
  (payload: string | undefined): void;
}

interface ISubscribeParams {
  receiver: INewsTopicReceiver;
}

class NewsTopic implements ISocketTopic {
  private client: Client;
  private receivers: Record<string, Set<INewsTopicReceiver> | undefined> = {};

  constructor(props: IProps) {
    const { client } = props;

    this.client = client;
  }

  public subscribe = (params: ISubscribeParams) => {
    const { receiver } = params;

    const { subPath, topicId } = this.mapTopicIds();

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]) {
      this.receivers[subPath]?.add(receiver);
    } else {
      this.receivers[subPath] = new Set([receiver]);

      try {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: topicId
        });
      } catch (err) {}
    }
  };

  public unsubscribe = (params: ISubscribeParams) => {
    const { receiver } = params;

    const { subPath, topicId } = this.mapTopicIds();

    if (!this.client) {
      return;
    }

    this.receivers[subPath]?.delete(receiver);

    if (this.receivers[subPath]?.size === 0) {
      try {
        this.client.unsubscribe(topicId);
      } catch (err) {}

      this.receivers[subPath] = undefined;
    }
  };

  public clearSubscriptions = () => {
    this.receivers = {};
  };

  public reconnect = () => {
    const { receivers } = this;

    if (receivers) {
      Object.keys(receivers).forEach((subPath) => {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: formatTopicId(subPath)
        });
      });
    }
  };

  private onReceive = (id: string, message: IMessage) => {
    const receivers = this.receivers[id];

    if (!receivers || !message) {
      return;
    }

    const payload = JSON.parse(message.body) as string;

    receivers.forEach((receiver) => receiver(payload));
  };

  private mapTopicIds = () => {
    const language = 'en-gb'; // To get from storage after integrating multi language
    const subPath = `news/${language}`;

    return {
      subPath,
      topicId: formatTopicId(subPath)
    };
  };
}

export default NewsTopic;
